import React, { useEffect, useState } from "react";
import { api, fmtPKR } from "../../lib/api";
import { Activity, Users, Receipt, MessageCircle, AlertTriangle } from "lucide-react";

function Stat({ icon: Icon, label, value, hint, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-800 border-slate-700 text-slate-200",
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-300",
    sky: "bg-sky-500/10 border-sky-500/30 text-sky-300",
  };
  return (
    <div data-testid={testid} className="bg-[#151A23] border border-slate-800 rounded-md p-5">
      <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${tones[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-4 text-xs text-slate-500 uppercase tracking-wide font-semibold">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold tabular text-white">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics/usage").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-white">Usage Analytics</h1>
        <p className="text-slate-400 mt-1">Daily active shops, feature usage, and churn signals.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Active shops (7d)" value={data.active_shops_7d} tone="emerald" testid="an-7d" />
        <Stat icon={Users} label="Active shops (30d)" value={data.active_shops_30d} tone="sky" testid="an-30d" />
        <Stat icon={Receipt} label="Payments (30d)" value={data.payments_30d} tone="slate" testid="an-pay" />
        <Stat icon={Activity} label="Loans (30d)" value={data.loans_created_30d} tone="slate" testid="an-loans" />
        <Stat icon={MessageCircle} label="WA messages (30d)" value={data.whatsapp_30d} tone="slate" testid="an-wa" />
        <Stat icon={AlertTriangle} label="Churn risk shops" value={data.inactive_shops?.length || 0} hint=">30d no activity" tone="amber" testid="an-churn" />
      </div>

      <div className="bg-[#151A23] border border-slate-800 rounded-md">
        <div className="p-4 border-b border-slate-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <div className="font-display font-semibold text-white">Churn risk · shops with no activity in last 30 days</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Shop</th>
                <th className="text-left px-4 py-3 font-semibold">Owner</th>
                <th className="text-left px-4 py-3 font-semibold">Plan</th>
                <th className="text-left px-4 py-3 font-semibold">Created</th>
              </tr>
            </thead>
            <tbody>
              {(data.inactive_shops || []).length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-500">All active shops are healthy.</td></tr>}
              {(data.inactive_shops || []).map((s) => (
                <tr key={s.id} className="border-t border-slate-800" data-testid={`an-churn-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-white">{s.name}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs tabular">{s.owner_email}</td>
                  <td className="px-4 py-3 capitalize text-slate-300">{s.plan_id}</td>
                  <td className="px-4 py-3 text-xs tabular text-slate-400">{s.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
